import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid2, TextField } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { shareLink } from './_api';
import { IShareStudyForm, IShareStudyResponse } from './_types';
import useValidationSchema from './_formShareStudy';
import { TourStudyDetail } from './TourStudyDetail';
import Header from 'components/Header/Header';
import { Papeer } from 'components/Papeer/Papeer';
import { EntityButtons } from 'components/Form/EntityButtons/EntityButtons';
import FormInput from 'components/Form/Input/Input';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { getSettingValueForVariable } from 'utils/products';
import { printOrDownloadBase64String } from 'utils/hooks/useApp';
import { setTourControlPropsToStore } from 'store/reducers/appReducer';

const ShareStudyForm: React.FC<IShareStudyForm> = ({ toggleDialog, study }) => {
  const { t } = useTranslation('StudyDetail');
  const { viewer } = useAppInfo();

  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const { toggleLoader } = useAppGlobals();

  const [shareStudyResponse, setShareStudyResponse] = useState<IShareStudyResponse | null>(null);

  const getValidityDays = () => {
    const validityDays = getSettingValueForVariable(
      get(viewer, 'systemSetting', []),
      'shareLinkValidity',
    );
    return validityDays ? Number(validityDays) : 31;
  };

  const { FormSchema } = useValidationSchema(getValidityDays());

  const methods = useForm<any>({
    resolver: yupResolver(FormSchema),
  });
  const { handleSubmit, reset } = methods;
  const dispatch = useDispatch();

  const clearAction = () => {
    toggleDialog();
    dispatch(setTourControlPropsToStore({ isTourOpen: false }));
  };

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();
    const prepareValues = {
      ...values,
      study: {
        accessionNumber: get(study, 'accessionNumber', ''),
        studyInstanceUid: get(study, 'studyInstanceUid', ''),
        archiveId: get(study, 'archive.id', 0),
      },
    };
    const response = await shareLink(prepareValues);
    if (response) {
      setShareStudyResponse(response);
      addSuccessAlert(t('successCreateLink'));
    } else {
      addErrorAlert(t('errorCreateLink'));
    }
    toggleLoader(false);
  });

  const generateRandomPin = () => {
    return Math.floor(1000 + Math.random() * 9000); // Generates 4-digit PIN
  };

  useEffect(() => {
    reset({ validityDays: getValidityDays(), pin: generateRandomPin() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderedSteps = () => {
    return <TourStudyDetail type="shareStudy" />;
  };

  const downloadPdf = (base64PdfString: string) => {
    if (base64PdfString) {
      const contentType = 'application/pdf';
      const fileName = 'filename.pdf';
      printOrDownloadBase64String(base64PdfString, contentType, fileName);
    }
  };

  const copyText = (text: string) => navigator.clipboard.writeText(text);

  return (
    <>
      <Header
        title={t('action.shareStudy')}
        TourComponent={shareStudyResponse ? null : renderedSteps()}
      />
      <Papeer>
        {shareStudyResponse ? (
          <Grid2 container={true} alignItems="flex-end">
            <Grid2 size={{ xs: 8 }} sx={{ my: 1 }}>
              <TextField
                label={t('shareStudyLink')}
                defaultValue={shareStudyResponse.link}
                size="small"
                fullWidth={true}
                disabled={true}
              />
            </Grid2>
            <Grid2 size={{ xs: 3 }} sx={{ my: 1, ml: 1 }}>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => copyText(shareStudyResponse.link)}
              >
                <ContentCopyIcon sx={{ mr: 1 }} />
                {t('Form:copy')}
              </Button>
            </Grid2>
            <Grid2 size={{ xs: 8 }} sx={{ my: 1 }}>
              <TextField
                label={t('pin')}
                defaultValue={shareStudyResponse.pin}
                size="small"
                fullWidth={true}
                disabled={true}
              />
            </Grid2>
            <Grid2 size={{ xs: 3 }} sx={{ my: 1, ml: 1 }}>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => copyText(shareStudyResponse.pin)}
              >
                <ContentCopyIcon sx={{ mr: 1 }} />
                {t('Form:copy')}
              </Button>
            </Grid2>
            <Grid2 size={{ xs: 8 }} sx={{ my: 1 }}>
              <TextField
                label={t('validityDays')}
                defaultValue={shareStudyResponse.validityDays}
                type="number"
                size="small"
                fullWidth={true}
                disabled={true}
              />
            </Grid2>
            <Grid2 size={{ xs: 8 }} sx={{ my: 1 }}>
              <TextField
                label={t('shareStudyPdf')}
                defaultValue={shareStudyResponse.pdf}
                size="small"
                fullWidth={true}
                multiline={true}
                maxRows={1}
                disabled={true}
              />
            </Grid2>
            <Grid2 size={{ xs: 3 }} sx={{ my: 1, ml: 1 }}>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => downloadPdf(shareStudyResponse.pdf)}
              >
                <PrintIcon sx={{ mr: 1 }} />
                {t('Form:print')}
              </Button>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }} style={{ marginTop: 16 }}>
                <Button
                  color="inherit"
                  variant="contained"
                  onClick={clearAction}
                  data-tour="closeButton"
                  data-pw="close-button"
                >
                  {t('close')}
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
              <Grid2 container={true} alignItems="flex-end">
                <Grid2 size={{ xs: 12 }} data-tour="study-detail-share-study-pin">
                  <FormInput name="pin" label={t('pin')} required={true} />
                </Grid2>
                <Grid2 size={{ xs: 12 }} data-tour="study-detail-share-study-validity-days">
                  <FormInput
                    name="validityDays"
                    label={t('validityDays')}
                    type="number"
                    required={true}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <EntityButtons
                    confirmReplaceSave={true}
                    toggleDialog={clearAction}
                    otherStyles={{ marginTop: 16 }}
                  />
                </Grid2>
              </Grid2>
            </form>
          </FormProvider>
        )}
      </Papeer>
    </>
  );
};

export default ShareStudyForm;
